import React, {
  useState,
  useRef,
  KeyboardEvent,
  useEffect,
  useContext,
} from 'react';
import { useTranslation } from 'react-i18next';
import {
  ArrowUp,
  Images,
  Loader2,
  CircleX,
  Box,
} from 'lucide-react';
import {
  cn,
  DEFAULT_PARAMETRIC_MODEL,
  DEFAULT_PARAMETRIC_MODEL_SUPPORTS_THINKING,
} from '@/lib/utils';
import { Content, Conversation } from '@shared/types';
import { MessageItem } from '@/types/misc';
import { useToast } from '@/hooks/use-toast';
import { useMutation } from '@tanstack/react-query';
import { storage } from '@/lib/firebase';
import { ref, uploadBytes, deleteObject } from 'firebase/storage';
import {
  getConversationImagePath,
  getConversationMeshPath,
  getConversationMeshRenderPath,
} from '@/lib/firebaseAssetPaths';
import { Textarea } from './ui/textarea';

import { useItemSelection } from '@/hooks/useItemSelection';
import { AnimatePresence, motion } from 'framer-motion';
import { processSTL, isValidSTL } from '@/utils/meshUtils';
import { MeshFilesContext } from '@/contexts/MeshFilesContext';

interface TextAreaChatProps {
  onSubmit: (content: Content) => void;
  placeholder?: string;
  disabled?: boolean;
  conversation: Pick<Conversation, 'id' | 'user_id'>;
  inputRef?: React.MutableRefObject<{ setInput: (text: string) => void } | null>;
}

const VALID_IMAGE_FORMATS = [
  'image/jpeg',
  'image/jpg',
  'image/png',
  'image/webp',
];

function TextAreaChat({
  onSubmit,
  placeholder,
  disabled = false,
  conversation,
  inputRef,
}: TextAreaChatProps) {
  const { t } = useTranslation('common');
  const resolvedPlaceholder = placeholder ?? t('chat.welcomePlaceholder');
  const [isFocused, setIsFocused] = useState(false);
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const [input, setInput] = useState('');

  // Expose setInput to parent via inputRef
  useEffect(() => {
    if (inputRef) {
      inputRef.current = { setInput };
    }
  }, [inputRef]);
  const [isDragging, setIsDragging] = useState(false);
  const [isDragHover, setIsDragHover] = useState(false);
  const [dropMessageOpacityClass, setDropMessageOpacityClass] = useState(
    'opacity-0 pointer-events-none',
  );
  const [dropMessageTransitionClass, setDropMessageTransitionClass] =
    useState('');
  const prevIsDraggingRef = useRef(isDragging);
  const { toast } = useToast();
  const { images, setImages, meshUpload, setMeshUpload } = useItemSelection();
  // MeshFilesContext is optional (only available in EditorView)
  const meshFilesContext = useContext(MeshFilesContext);

  // Refs for the two hot-zones
  const topDropZoneRef = useRef<HTMLDivElement>(null);
  const textAreaContainerZoneRef = useRef<HTMLDivElement>(null);

  // Animation variants for image thumbnails
  const itemAnimationVariants = {
    initial: { opacity: 0, scale: 0.8 },
    animate: {
      opacity: 1,
      scale: 1,
    },
    exit: {
      opacity: 0,
      scale: 0.8,
    },
  };

  // The text currently shown in the placeholder (animates)
  const [placeholderAnim, setPlaceholderAnim] = useState('');
  const [placeholderOpacity, setPlaceholderOpacity] = useState(1);
  const placeholderRef = useRef('');

  // Shared helper that performs the crossfade animation
  const startCrossfade = (target: string) => {
    placeholderRef.current = target;

    // Start fade out
    setPlaceholderOpacity(0);

    // After fade out, update text and fade in
    setTimeout(() => {
      setPlaceholderAnim(target);
      setPlaceholderOpacity(1);
    }, 150);
  };

  // Kick off crossfade effect whenever the target placeholder changes
  useEffect(() => {
    startCrossfade(resolvedPlaceholder);
  }, [resolvedPlaceholder, placeholderAnim]);

  const handleSubmit = async () => {
    // Filter out mesh renders from user images
    const userImages = images.filter((img) => img.source !== 'mesh-render');
    const hasMeshProcessing = meshUpload?.isProcessing;

    // Debug the early return conditions
    const hasNoContent =
      userImages.length === 0 && !input?.trim() && !meshUpload;
    const hasUploadingImages = images.some((img) => img.isUploading);

    if (hasNoContent || disabled || hasUploadingImages || hasMeshProcessing) {
      return;
    }

    const content: Content = {
      ...(input.trim() !== '' && { text: input.trim() }),
      ...(userImages.length > 0 && { images: userImages.map((img) => img.id) }),
      // Include mesh data if an STL was uploaded
      ...(meshUpload && {
        mesh: { id: meshUpload.id, fileType: 'stl' },
        meshRenders: meshUpload.renderIds,
        meshBoundingBox: meshUpload.boundingBox,
        meshFilename: meshUpload.filename,
      }),
      model: DEFAULT_PARAMETRIC_MODEL,
      thinking: DEFAULT_PARAMETRIC_MODEL_SUPPORTS_THINKING,
    };
    onSubmit(content);
    setInput('');
    setImages([]);
    setMeshUpload(null);
  };

  const { mutateAsync: uploadImageAsync } = useMutation({
    mutationFn: async ({ file, id }: { file: File; id: string }) => {
      const storageRef = ref(
        storage,
        getConversationImagePath(conversation.user_id, conversation.id, id),
      );
      await uploadBytes(storageRef, file, { contentType: file.type });

      const reader = new FileReader();
      const urlPromise = new Promise((resolve) => {
        reader.onload = () => {
          resolve(reader.result as string);
        };
      });
      reader.readAsDataURL(file);
      const url = (await urlPromise) as string;

      return url;
    },
    onError: () => {
      toast({
        title: t('toast.error'),
        description: t('chat.imageUploadFailed'),
        variant: 'destructive',
      });
    },
  });

  // Upload a blob (used for STL renders)
  const { mutateAsync: uploadBlobAsync } = useMutation({
    mutationFn: async ({ blob, id }: { blob: Blob; id: string }) => {
      const storageRef = ref(
        storage,
        getConversationMeshRenderPath(conversation.user_id, conversation.id, id),
      );
      await uploadBytes(storageRef, blob, {
        contentType: blob.type || 'image/png',
      });

      return URL.createObjectURL(blob);
    },
    onError: () => {
      toast({
        title: t('toast.error'),
        description: t('chat.renderUploadFailed'),
        variant: 'destructive',
      });
    },
  });

  // Process and upload an STL file
  const handleSTLUpload = async (file: File) => {
    const meshId = crypto.randomUUID();
    // Sanitize filename for OpenSCAD (remove spaces, special chars)
    const safeFilename =
      file.name
        .toLowerCase()
        .replace(/[^a-z0-9._-]/g, '_')
        .replace(/\.stl$/i, '') + '.stl';

    // Set processing state with placeholder values
    setMeshUpload({
      id: meshId,
      filename: safeFilename,
      boundingBox: { x: 0, y: 0, z: 0 },
      renderIds: [],
      isProcessing: true,
      fileContent: file, // Store the actual file content for OpenSCAD import
    });

    try {
      // Process STL to get renders and bounding box
      const { boundingBox, renders } = await processSTL(file);

      // Upload the STL file to storage (for persistence/sharing)
      const stlRef = ref(
        storage,
        getConversationMeshPath(
          conversation.user_id,
          conversation.id,
          meshId,
          'stl',
        ),
      );
      await uploadBytes(stlRef, file, { contentType: file.type || 'model/stl' });

      // Upload renders and collect their IDs
      const renderIds: string[] = [];
      const renderUrls: string[] = [];

      for (let i = 0; i < renders.length; i++) {
        const renderId = `render-${meshId}-${i}.png`;
        renderIds.push(renderId);

        const url = await uploadBlobAsync({ blob: renders[i], id: renderId });
        renderUrls.push(url);
      }

      // Add render thumbnails to images for display
      const renderItems: MessageItem[] = renderIds.map((id, index) => ({
        id,
        isUploading: false,
        url: renderUrls[index],
        source: 'mesh-render' as const,
        meshId,
      }));

      setImages((prev) => [...prev, ...renderItems]);

      // Update mesh upload state with final data (keep file content)
      setMeshUpload({
        id: meshId,
        filename: safeFilename,
        boundingBox,
        renderIds,
        isProcessing: false,
        fileContent: file,
      });

      // Store mesh file in context for OpenSCAD import (if context available)
      if (meshFilesContext) {
        meshFilesContext.setMeshFile(safeFilename, file);
      }
    } catch (error) {
      console.error('Error processing STL:', error);
      setMeshUpload(null);
      toast({
        title: t('chat.modelProcessingError'),
        description:
          error instanceof Error ? error.message : 'STLファイルの処理に失敗しました',
        variant: 'destructive',
      });
    }
  };

  const addItems = async (files: FileList) => {
    const newItems = Array.from(files);
    let hasSmallImages = false;
    let hasLargeImages = false;
    let hasInvalidImages = false;
    const MAX_FILE_SIZE = 100 * 1024 * 1024; // 100MB limit

    // Separate STL files from images
    const stlFiles: File[] = [];
    const imageFiles: File[] = [];

    for (const file of newItems) {
      if (isValidSTL(file)) {
        stlFiles.push(file);
      } else {
        imageFiles.push(file);
      }
    }

    // Handle STL files (only allow one at a time for now)
    if (stlFiles.length > 0) {
      if (meshUpload) {
        toast({
          title: t('chat.singleModelTitle'),
          description: t('chat.singleModelDesc'),
        });
      } else {
        // Process first STL file
        await handleSTLUpload(stlFiles[0]);
        if (stlFiles.length > 1) {
          toast({
            title: t('chat.multipleModelsTitle'),
            description: t('chat.multipleModelsDesc'),
          });
        }
      }
    }

    // Process image files
    const validImages = await Promise.all(
      imageFiles.map(async (file) => {
        // First check file type Must be jpeg, png, gif, or webp.
        if (!file.type.includes('image')) {
          return null;
        }

        if (!VALID_IMAGE_FORMATS.includes(file.type)) {
          hasInvalidImages = true;
          return null;
        }

        // Check file size
        if (file.size > MAX_FILE_SIZE) {
          hasLargeImages = true;
          return null;
        }

        // Check dimensions asynchronously
        return new Promise<File | null>((resolve) => {
          const img = new Image();
          img.src = URL.createObjectURL(file);
          img.onload = () => {
            if (img.naturalWidth < 256 || img.naturalHeight < 256) {
              hasSmallImages = true;
              resolve(null); // Image too small
            } else {
              resolve(file); // Valid image
            }
            URL.revokeObjectURL(img.src);
          };
          img.onerror = () => {
            resolve(null); // Invalid image
            URL.revokeObjectURL(img.src);
          };
        });
      }),
    );

    // Filter out null values (invalid images)
    const filteredImages = validImages.filter(
      (img): img is File => img !== null,
    );

    // Show specific errors first, then generic error only if there are truly invalid file types
    if (hasSmallImages) {
      toast({
        title: t('chat.imageTooSmallTitle'),
        description: t('chat.imageTooSmallDesc'),
      });
    } else if (hasLargeImages) {
      toast({
        title: t('chat.imageTooLargeTitle'),
        description: t('chat.imageTooLargeDesc'),
      });
    } else if (hasInvalidImages) {
      toast({
        title: t('chat.invalidImageTitle'),
        description: t('chat.invalidImageDesc'),
      });
    }

    // Upload each valid image immediately
    filteredImages.forEach(async (file) => {
      const tempId = crypto.randomUUID();
      const url = URL.createObjectURL(file);
      setImages((prevImages) => [
        ...prevImages,
        { id: tempId, isUploading: true, source: 'upload', url },
      ]);
      try {
        const signedUrl = await uploadImageAsync({ file, id: tempId });
        URL.revokeObjectURL(url);
        setImages((prevImages) =>
          prevImages.map((img) =>
            img.id === tempId
              ? { ...img, isUploading: false, url: signedUrl }
              : img,
          ),
        );
      } catch (error) {
        console.error('Error uploading image:', error);
        setImages((prevImages) =>
          prevImages.filter((img) => img.id !== tempId),
        );
      }
    });
  };

  const handleKeyDown = (e: KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSubmit();
    }
  };

  const handlePaste = (event: React.ClipboardEvent<HTMLTextAreaElement>) => {
    const files = event.clipboardData.files;
    if (files && files.length > 0) {
      event.preventDefault();
      addItems(files);
    }
  };

  const handleDrop = async (event: React.DragEvent<HTMLDivElement>) => {
    event.preventDefault(); // Signal that this component handled the drop
    const droppedFiles = event.dataTransfer?.files;

    let shouldAddItems = false;
    const target = event.target as Node;

    if (
      topDropZoneRef.current?.contains(target) ||
      textAreaContainerZoneRef.current?.contains(target)
    ) {
      shouldAddItems = true;
    }

    if (shouldAddItems && droppedFiles && droppedFiles.length > 0) {
      await addItems(droppedFiles);
    }

    // Always reset drag states, regardless of whether files were added
    setIsDragging(false);
    setIsDragHover(false);
  };

  const handleImageRemoved = async (image: MessageItem) => {
    if (!image.isUploading) {
      // If this is a mesh render, clear the entire mesh upload
      if (image.source === 'mesh-render' && image.meshId) {
        try {
          // Remove all renders and the STL file
          const pathsToRemove = [
            getConversationMeshPath(
              conversation.user_id,
              conversation.id,
              image.meshId,
              'stl',
            ),
            ...images
              .filter((img) => img.meshId === image.meshId)
              .map(
                (img) =>
                  getConversationMeshRenderPath(
                    conversation.user_id,
                    conversation.id,
                    img.id,
                  ),
              ),
          ];
          await Promise.all(
            pathsToRemove.map((path) => deleteObject(ref(storage, path)).catch(() => {})),
          );
        } catch (error) {
          console.error('Error removing mesh files:', error);
        }
        // Remove all renders for this mesh from images
        setImages((prevImages) =>
          prevImages.filter((img) => img.meshId !== image.meshId),
        );
        setMeshUpload(null);
        return;
      }

      // Only try to remove from storage if the item has been uploaded
      if (image.source === 'upload') {
        try {
          const imageRef = ref(
            storage,
            getConversationImagePath(
              conversation.user_id,
              conversation.id,
              image.id,
            ),
          );
          await deleteObject(imageRef);
        } catch (error) {
          console.error('Error removing image:', error);
        }
      }
      setImages((prevImages) =>
        prevImages.filter((img) => img.id !== image.id),
      );
    }
  };

  // Add global drag-and-drop listeners so that dropping files anywhere on the page is handled.
  useEffect(() => {
    // Prevent default browser behaviour (e.g. opening the image in a new tab)
    const preventDefaults = (e: DragEvent) => {
      e.preventDefault();
    };

    const handleDragEnter = (e: DragEvent) => {
      e.preventDefault();
      setIsDragging(true);
      // When a drag operation newly enters the window, assume it's not hovering
      // over a specific component's hot-zone yet. Hot-zones will override this.
      setIsDragHover(false);
    };

    const handleDragLeave = (e: DragEvent) => {
      e.preventDefault();
      // When leaving the window entirely (relatedTarget is null), reset dragging state
      if (
        (e as unknown as { relatedTarget: Node | null }).relatedTarget === null
      ) {
        setIsDragging(false);
        setIsDragHover(false);
      }
    };

    const handleDropGlobal = async (e: DragEvent) => {
      // If a more specific drop handler (like in TextAreaChat) already handled this event
      // and called e.preventDefault(), the global handler should not interfere.
      if (e.defaultPrevented) {
        return;
      }

      // If we're here, the drop occurred outside a component that handled it.
      // Prevent the browser's default action (e.g., opening the file).
      e.preventDefault();

      // For a global drop outside handled areas, we don't add items.
      // We just clear the overall drag UI state.
      setIsDragging(false);
      setIsDragHover(false);
      // NO call to addItems() here.
    };

    window.addEventListener('dragenter', handleDragEnter);
    window.addEventListener('dragover', preventDefaults);
    window.addEventListener('dragleave', handleDragLeave);
    window.addEventListener('drop', handleDropGlobal);

    return () => {
      window.removeEventListener('dragenter', handleDragEnter);
      window.removeEventListener('dragover', preventDefaults);
      window.removeEventListener('dragleave', handleDragLeave);
      window.removeEventListener('drop', handleDropGlobal);
    };
  }, []);

  useEffect(() => {
    if (images.length === 0) {
      // Case 1: No items are present in the drop zone
      if (isDragging) {
        // If dragging, the message should be visible and can transition
        setDropMessageOpacityClass('opacity-100');
        setDropMessageTransitionClass(
          'transition-opacity duration-200 ease-in-out',
        );
      } else {
        // Not dragging. Message should be hidden.
        if (prevIsDraggingRef.current) {
          // If it WAS dragging and now it's not, it should fade out.
          setDropMessageTransitionClass(
            'transition-opacity duration-200 ease-in-out',
          );
          setDropMessageOpacityClass('opacity-0 pointer-events-none');
        } else {
          // If it was NOT dragging and still isn't (e.g., mounting fresh after image removal),
          // it should be instantly hidden, no transition.
          setDropMessageTransitionClass('');
          setDropMessageOpacityClass('opacity-0 pointer-events-none');
        }
      }
    } else {
      // Case 2: Items ARE present in the drop zone, message should be instantly hidden.
      setDropMessageTransitionClass('');
      setDropMessageOpacityClass('opacity-0 pointer-events-none');
    }
    prevIsDraggingRef.current = isDragging;
  }, [isDragging, images.length]);

  return (
    <div
      className="group relative"
      onDrop={handleDrop}
      onDragEnter={(event) => {
        event.preventDefault();
        setIsDragging(true);
      }}
      onDragOver={(event) => {
        event.preventDefault();
        setIsDragging(true);
      }}
      onDragLeave={(event) => {
        event.preventDefault();
        // If the drag operation leaves the bounds of this entire component,
        // then isDragHover should definitely be false.
        if (!event.currentTarget.contains(event.relatedTarget as Node)) {
          setIsDragHover(false);
        }
      }}
      onClick={() => {
        textareaRef.current?.focus();
      }}
    >
      <div
        ref={topDropZoneRef}
        className={cn(
          'mx-auto flex w-[95%] min-w-52 overflow-hidden rounded-t-xl border-x border-t',
          'transition-[height,opacity,border-color,background-color] duration-200 ease-in-out',
          disabled
            ? 'h-0 border-transparent bg-transparent opacity-0'
            : !isDragging && images.length === 0
              ? 'h-0 border-transparent bg-transparent opacity-0'
              : isDragging
                ? isDragHover
                  ? 'h-20 border-brand-indigo bg-brand-teal/40 opacity-100'
                  : 'h-20 border-brand-indigo/60 bg-brand-teal-soft/30 opacity-100'
                : images.length > 0
                  ? 'h-20 border-synergy-neutral-700 bg-synergy-neutral-900 opacity-100'
                  : 'h-0 border-transparent bg-transparent opacity-0',
        )}
        onDragEnter={(event) => {
          if (isDragging) {
            event.preventDefault();
            setIsDragHover(true);
          }
        }}
        onDragOver={(event) => {
          if (isDragging) {
            event.preventDefault();
            setIsDragHover(true);
          }
        }}
        onDragLeave={(event) => {
          if (isDragging) {
            event.preventDefault();
            if (!event.currentTarget.contains(event.relatedTarget as Node)) {
              setIsDragHover(false);
            }
          }
        }}
      >
        {!disabled && (
          <>
            {/* Case 1: Dragging, and items are ALREADY present -> Show "Add more images" prompt */}
            {isDragging && images.length > 0 ? (
              <div
                className={cn(
                  'flex h-full w-full flex-row items-center justify-center gap-2', // Ensure it fills parent
                  // Opacity is handled by the parent's transition when it appears/disappears due to isDragging
                )}
              >
                <Images
                  className="h-5 w-5"
                  style={{
                    color: isDragHover ? '#170C79' : 'rgba(23, 12, 121, 0.7)',
                  }}
                />
                <p
                  className="text-sm font-semibold"
                  style={{
                    color: isDragHover ? '#170C79' : 'rgba(23, 12, 121, 0.7)',
                  }}
                >
                  {t('chat.addImageHere')}
                </p>
              </div>
            ) : /* Case 2: No items (images are zero) -> Show original "Drop images and 3D models here" logic */
            images.length === 0 ? (
              <div
                className={cn(
                  'flex h-full w-full flex-row items-center justify-center gap-2', // Ensure it fills parent
                  dropMessageTransitionClass,
                  dropMessageOpacityClass,
                )}
              >
                <Images
                  className="h-5 w-5"
                  style={{
                    color: isDragHover ? '#170C79' : 'rgba(23, 12, 121, 0.7)',
                  }}
                />
                <p
                  className="text-sm font-semibold"
                  style={{
                    color: isDragHover ? '#170C79' : 'rgba(23, 12, 121, 0.7)',
                  }}
                >
                  {t('chat.dropImagesOrModels')}
                </p>
              </div>
            ) : (
              /* Case 3: Items are present, and NOT dragging -> Show thumbnails */
              images.length > 0 && (
                <div
                  className={cn(
                    'flex w-full items-center gap-4 overflow-x-auto overflow-y-hidden p-4',
                    // Opacity dimming logic can remain if desired, or be simplified
                    isDragging && images.length > 0
                      ? 'opacity-60'
                      : 'opacity-100',
                    'transition-opacity duration-150',
                  )}
                >
                  <AnimatePresence>
                    {' '}
                    {images.map((image) => (
                      <motion.div
                        key={`image-${image.id}`}
                        className="relative h-12 w-12 flex-shrink-0"
                        variants={itemAnimationVariants}
                        initial="initial"
                        animate="animate"
                        exit="exit"
                        layout
                      >
                        <img
                          src={image.url}
                          alt={
                            image.source === 'mesh-render'
                              ? t('chat.modelViewAlt')
                              : t('chat.imageAlt')
                          }
                          className="h-12 w-12 rounded-md object-cover"
                        />
                        {/* Show 3D icon badge for mesh renders */}
                        {image.source === 'mesh-render' && (
                          <div className="absolute bottom-0 left-0 rounded-br-md rounded-tl-md bg-synergy-blue/80 p-0.5">
                            <Box className="h-3 w-3 text-white" />
                          </div>
                        )}
                        {image.isUploading && (
                          <div className="absolute inset-0 flex items-center justify-center rounded-md bg-black/50">
                            <Loader2 className="h-4 w-4 animate-spin text-white" />
                          </div>
                        )}
                        <button
                          onClick={() => handleImageRemoved(image)}
                          disabled={image.isUploading}
                          className={cn(
                            'absolute right-[-0.50rem] top-[-0.50rem] rounded-full border border-synergy-neutral-400 bg-synergy-neutral-400 text-synergy-text-primary transition-colors duration-200 hover:border-synergy-neutral-500 hover:bg-synergy-neutral-500',
                            image.isUploading && 'opacity-50',
                          )}
                        >
                          <CircleX className="h-4 w-4 stroke-[1.5]" />
                        </button>
                      </motion.div>
                    ))}
                  </AnimatePresence>
                </div>
              )
            )}
          </>
        )}
      </div>
      <div
        ref={textAreaContainerZoneRef}
        className={cn(
          'relative rounded-2xl border shadow-sm',
          isFocused
            ? 'border-synergy-blue/60 shadow-synergy-blue/20 shadow-md ring-2 ring-synergy-blue/30'
            : 'border-synergy-neutral-600/30 hover:border-synergy-neutral-500/50 hover:shadow-md',
          'bg-white/80 backdrop-blur-sm transition-all duration-300',
        )}
        onDragEnter={(event) => {
          if (isDragging) {
            event.preventDefault();
            setIsDragHover(true);
          }
        }}
        onDragOver={(event) => {
          if (isDragging) {
            event.preventDefault();
            setIsDragHover(true);
          }
        }}
        onDragLeave={(event) => {
          if (isDragging) {
            event.preventDefault();
            if (!event.currentTarget.contains(event.relatedTarget as Node)) {
              setIsDragHover(false);
            }
          }
        }}
      >
        <div className="flex select-none items-end gap-2 p-2">
          <div className="flex min-w-0 flex-1 flex-col">
            <div className="relative grid w-full">
              <Textarea
                disabled={disabled}
                value={input}
                ref={textareaRef}
                translate="no"
                onKeyDown={handleKeyDown}
                onPaste={handlePaste}
                onBlur={() => setIsFocused(false)}
                onFocus={() => setIsFocused(true)}
                onChange={(e) => {
                  setInput(e.target.value);
                }}
                placeholder={placeholderAnim}
                className="hide-scrollbar z-40 block h-auto min-h-0 w-full resize-none overflow-hidden whitespace-pre-line break-words border-none bg-transparent px-3 py-2 text-sm text-synergy-text-primary outline-none transition-all duration-500 placeholder:text-synergy-text-tertiary placeholder:opacity-[var(--placeholder-opacity)] placeholder:transition-all placeholder:duration-300 placeholder:ease-in-out focus:border-0 focus:shadow-none focus:outline-none focus:ring-0 focus-visible:outline-none focus-visible:ring-0 focus-visible:ring-offset-0 sm:px-4"
                style={
                  {
                    '--placeholder-opacity': placeholderOpacity,
                    gridArea: '1 / -1',
                  } as React.CSSProperties
                }
                rows={1}
              />
              <div
                className="pointer-events-none col-start-1 row-start-1 w-full overflow-hidden whitespace-pre-wrap break-words px-3 py-2 text-sm opacity-0 sm:px-4"
                style={{ gridArea: '1 / -1' }}
              >
                <span>{input}</span>
                <br />
              </div>
            </div>
          </div>
          {/* Submit button - right side, aligned to bottom */}
          <button
            onClick={() => {
              handleSubmit();
            }}
            className={cn(
              'mb-1 flex h-9 w-9 flex-shrink-0 transform items-center justify-center rounded-xl bg-synergy-blue p-1 text-white transition-all duration-200 hover:scale-105 hover:bg-synergy-blue/90 disabled:opacity-40 disabled:hover:scale-100 disabled:hover:bg-synergy-blue',
              (images.some((img) => img.isUploading) ||
                meshUpload?.isProcessing) &&
                'opacity-40',
            )}
            disabled={
              (images.filter((img) => img.source !== 'mesh-render').length ===
                0 &&
                !input?.trim() &&
                !meshUpload) ||
              images.some((img) => img.isUploading) ||
              meshUpload?.isProcessing ||
              disabled
            }
          >
            <ArrowUp className="h-5 w-5" />
          </button>
        </div>
      </div>
    </div>
  );
}

export default TextAreaChat;
